import type { Condition } from '@locus-taxy/encore-grid';

/**
 * Query param that narrows an entity grid to a caller-supplied condition, so any
 * feature can link into a filtered grid. Page-scoped `p.` prefix (DP-6873, see
 * `useDashboardNavigate`): the blind query merge must not carry it onto the next
 * in-app navigation, where it would filter an unrelated grid.
 *
 * The condition REPLACES the grid's own base condition (the entity view
 * config's), it is not ANDed with it: encore-grid resolves
 * `baseCondition ?? entityConfig.baseCondition`. That is intended — a linked
 * set, such as an impact-preview group, must show exactly the rows its count
 * promised. A caller that needs the grid's scope as well must include it in
 * the condition it puts here.
 */
export const BASE_CONDITION_PARAM = 'p.baseCondition';

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * A condition the grid can resolve without throwing: a plain object with a
 * string `operator`, whose `values` (if present) is an array and whose
 * `conditions` (if present) is an array of conditions of the same kind.
 * encore-grid's resolver calls `.map` on both, so a wrong shape there would
 * crash the grid instead of leaving it unfiltered.
 */
function isCondition(value: unknown): value is Condition {
  if (!isRecord(value) || typeof value.operator !== 'string') {
    return false;
  }
  if (value.values !== undefined && !Array.isArray(value.values)) {
    return false;
  }
  if (value.conditions === undefined) {
    return true;
  }
  return Array.isArray(value.conditions) && value.conditions.every(isCondition);
}

/**
 * Parses the `p.baseCondition` value into a grid condition. The value is the
 * already URL-decoded JSON that react-router hands back, so it is not decoded
 * again. Anything that is not a well-formed condition at every level (see
 * `isCondition`) is rejected, and the grid then renders unfiltered.
 */
export function parseBaseConditionParam(
  value: string | null
): Condition | undefined {
  if (!value) {
    return undefined;
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(value);
  } catch {
    return undefined;
  }
  return isCondition(parsed) ? parsed : undefined;
}
